import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Form } from "semantic-ui-react";
import { useFormContext } from "react-hook-form";

// Actions
import CustomerIncidentAction from "@common/redux/actions/CustomerIncidentAction";
import CustomerFormAction from "@common/redux/actions/CustomerFormAction";
import LoginAction from "@common/redux/actions/LoginAction";

// Hooks and Helpers
import useCustomerIncidentForm from "@common/components/hooks/useCustomerIncidentForm";
import { getCurrentDateTime } from "@common/components/helpers/datetime";

// Components
import EmergencyServicesForm from "@common/components/shared/forms/EmergencyServicesForm";
import SelectComponent from "@common/components/shared/forms/SelectComponent";
import StandardHiddenInputs from "@common/components/shared/forms/StandardHiddenInputs";
import TextareaComponent from "@common/components/shared/forms/TextareaComponent";
import ToggleComponent from "@common/components/shared/forms/ToggleComponent";
import DateAndTimeGroup from "@common/components/shared/forms/DateAndTimeGroup";
import FormButtonsComponent from "@common/components/shared/forms/FormButtonsComponent";
import CustomerForm from "@common/components/shared/CustomerForm";
import UploadComponent from "@common/components/shared/forms/UploadComponent";
import { setComplianceFormDateTime } from "../../helpers/datetime";

const CustomerIncidentForm = (props) => {
  const formData = props.data;
  const siteGuid = props.loginData.siteGuid;

  const form = useFormContext();
  const {
    control,
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  const [
    watchInjuries,
    watchESCalled,
    watchServices,
    watchDidNotAttend,
    watchCctv,
  ] = watch([
    "injuries",
    "esCalled",
    "esTypes",
    "servicesDidNotAttend",
    "capturedByCctv",
  ]);

  const history = useHistory();
  const {
    onSubmit,
    onError,
    resetForm,
    isDesktop,
    onSelect,
    handleNewCustomer,
    handleEditCustomer,
    handleRemoveCustomer,
    handleExistingCustomer,
  } = useCustomerIncidentForm({
    formData,
    siteGuid,
    form,
    history,
  });

  useEffect(() => {
    if (!isDesktop) {
      setComplianceFormDateTime(setValue);
    }
  }, []);

  return (
    <Form
      className="form-padding"
      disabled
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <StandardHiddenInputs />
      <DateAndTimeGroup
        control={control}
        dateFormat="YYYY-MM-DD"
        errorsDate={errors.date}
        errorsTime={errors.time}
        maxDate={getCurrentDateTime()}
      />

      <SelectComponent
        name="customerIncidentType"
        label="Incident Type"
        placeholder="Select type of incident"
        control={control}
        error={errors.customerIncidentType}
        options={formData.customerIncidentTypes}
      />

      <CustomerForm
        onSelect={onSelect}
        handleNewCustomer={handleNewCustomer}
        handleEditCustomer={handleEditCustomer}
        handleExists={handleExistingCustomer}
        handleRemoveCustomer={handleRemoveCustomer}
        label="Persons involved in incident"
        placeholder="Search for person..."
        unknownNameLabel={"Add unknown customers"}
        selectContainerStyle="w-60"
      />

      <TextareaComponent
        name="description"
        label="Description"
        placeholder="Enter details of the incident and names of witnesses"
        register={register}
        error={errors.description}
        labelAbove={true}
      />

      <ToggleComponent
        control={control}
        name="capturedByCctv"
        label="Captured by CCTV?"
        error={errors.capturedByCctv}
      />
      {isDesktop && watchCctv && <UploadComponent />}

      <ToggleComponent
        control={control}
        name="injuries"
        label="Anyone injured?"
        error={errors.injuries}
      />

      {watchInjuries && (
        <TextareaComponent
          name="injuryDetails"
          label=" Details of injured (name, address, contact number/email)"
          placeholder="Enter details of injured person(s)"
          register={register}
          error={errors.injuryDetails}
          labelAbove={true}
        />
      )}

      {watchServices && (
        <EmergencyServicesForm
          policeCalled={watchServices.includes("police")}
          ambulanceCalled={watchServices.includes("ambulance")}
          eSCalled={watchESCalled}
          didNotAttend={watchDidNotAttend}
        />
      )}

      <TextareaComponent
        name="comments"
        label="Comments"
        placeholder="Please enter comments..."
        register={register}
        error={errors.comments}
        fieldStyle="py-1"
      />

      <FormButtonsComponent
        isValid={isValid}
        centered={true}
        resetForm={() => resetForm(false)}
      />
    </Form>
  );
};

const mapStateToProps = (state) => {
  return {
    data: CustomerIncidentAction.getStatePath(state),
    customerFormOptions: CustomerFormAction.getStatePath(state),
    loginData: LoginAction.getStatePath(state),
  };
};

export default connect(mapStateToProps)(CustomerIncidentForm);
